/**
 * productHistoryMock — synthetic data with the EXACT shape of
 * `GET /products/{upc}/spot-history` (see docs/backend/product-spot-history-spec.md).
 *
 * Exists so the Product Spot History modal can be built and reviewed before the
 * backend endpoint is deployed. Enabled with `VITE_PRODUCT_HISTORY_MOCK=true`.
 *
 * Forcing edge states from the UI (pick a product whose UPC ends in…):
 *   …0000 → empty result (no spots)
 *   …9999 → request error (exercises the retry state)
 * Every 7th spot has no image, to exercise the "Image not available" state.
 *
 * ⚠️ The data is fake. Never validate against Tableau with this on.
 */

export const PRODUCT_HISTORY_MOCK = import.meta.env.VITE_PRODUCT_HISTORY_MOCK === 'true';

const RETAILERS = ['Amigo', 'Econo', 'Selectos', 'Pueblo', 'Supermax', 'Mr Special'];
const VARIANTS = [
  'Orange', 'Baking Soda Citrus & Fruits', 'Lavanda', 'Lemon', 'Mango',
  'Navidad Pino', 'Navidad Red Berry & Mint', 'Pasion de Frutas', 'Ocean Paradise',
  'Fresh Spring', 'Energizing Lime', 'Antibacterial',
];
const PROMO_TYPES = ['Regular', 'Especial', '2x$', 'BOGO'];
const AD_TYPES = ['Shopper', 'Digital', 'Insert'];

/** Deterministic PRNG: the mock must not change between reloads. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function toISODay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDay(isoDay) {
  const [y, m, d] = String(isoDay).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/**
 * @param {{ upc: string, from: string, to: string, signal?: AbortSignal }} params
 */
export async function mockProductSpotHistory({ upc, from, to, signal }) {
  await delay(350, signal);

  const target = String(upc);
  if (target.endsWith('9999')) throw new Error('Mock: forced error for UPC ending in 9999');

  const empty = { upc: target, description: '', truncated: false, spots: [], products: [] };
  if (target.endsWith('0000')) return empty;

  const rand = mulberry32(hashString(target));
  const base = Number(target.slice(-5)) || 10000;

  // Product family: the target plus siblings that tend to share its spots.
  const family = VARIANTS.map((variant, i) => ({
    upc: i === 0 ? target : String(base * 100 + i).padStart(12, '0'),
    description: i === 0
      ? `Mock Product ${target}`
      : `Mock ${variant} Limpiador Multiusos Bot 28 Oz`,
    brand: 'Mock Brand',
    size: '28 Oz',
    // Some siblings are seasonal and almost never show up.
    weight: i === 0 ? 1 : (variant.startsWith('Navidad') ? 0.08 : 0.35 + rand() * 0.6),
  }));
  const membership = new Map(family.map((p) => [p.upc, []]));

  // Each retailer runs a weekly circular (Thursday) and features the product some weeks.
  const retailers = RETAILERS.filter(() => rand() < 0.6);
  if (!retailers.includes('Amigo')) retailers.push('Amigo');

  const spots = [];
  const start = parseDay(from);
  const end = parseDay(to);
  let nextId = 1_400_000 + (base % 10_000);

  const cursor = new Date(start);
  cursor.setDate(cursor.getDate() + ((4 - cursor.getDay() + 7) % 7));

  while (cursor <= end) {
    const day = toISODay(cursor);
    for (const retailer of retailers) {
      const weekRand = mulberry32(hashString(`${target}|${retailer}|${day}`));
      if (weekRand() > 0.35) continue;

      nextId += 1 + Math.floor(weekRand() * 4000);
      const spotId = nextId;
      spots.push({
        spotId,
        customername: retailer,
        startdate: day,
        pagenumber: 1 + Math.floor(weekRand() * 12),
        promotype: PROMO_TYPES[Math.floor(weekRand() * PROMO_TYPES.length)],
        adtype: AD_TYPES[Math.floor(weekRand() * AD_TYPES.length)],
        spotImage: spots.length % 7 === 6
          ? null
          : `https://placehold.co/640x420/png?text=Spot+${spotId}`,
      });

      for (const product of family) {
        if (product.upc === target || weekRand() < product.weight) {
          membership.get(product.upc).push(spotId);
        }
      }
    }
    cursor.setDate(cursor.getDate() + 7);
  }

  spots.sort((a, b) => (a.startdate === b.startdate ? b.spotId - a.spotId : a.startdate < b.startdate ? 1 : -1));

  return {
    upc: target,
    description: family[0].description,
    truncated: false,
    spots,
    products: family
      .filter((p) => membership.get(p.upc).length)
      .map(({ upc: productUpc, description, brand, size }) => ({
        upc: productUpc,
        description,
        brand,
        size,
        spotIds: membership.get(productUpc),
      })),
  };
}
